import { useCallback, useEffect } from 'react';

import type { AttributionData, StoredAttribution } from '~/types/onboarding';
import { ATTRIBUTION_STORAGE_KEY, ATTRIBUTION_TTL_MS } from '~/utils/attributionConstants';

/**
 * Captures attribution data from URL parameters on component mount.
 *
 * Implements idempotent capture with 30-minute TTL:
 * - Skips capture if valid unexpired attribution exists
 * - Stores empty attribution (all fields null) if no UTM params present
 * - Captures referrer and UTM params from current URL
 *
 * Storage key and TTL defined in attributionConstants.ts per §BASIC.2.
 *
 * @example
 * function LandingPage() {
 *   useAttributionCapture();
 *   return <div>Welcome</div>;
 * }
 */
export function useAttributionCapture(): void {
  useEffect(() => {
    // Check for existing valid attribution
    const existingAttribution = getStoredAttribution();
    if (existingAttribution !== null) {
      // Valid unexpired attribution exists, skip capture
      return;
    }

    // Capture current URL parameters
    const searchParams = new URLSearchParams(window.location.search);

    const attributionData: AttributionData = {
      referrer: document.referrer || null,
      utmSource: searchParams.get('utm_source') || null,
      utmMedium: searchParams.get('utm_medium') || null,
      utmCampaign: searchParams.get('utm_campaign') || null,
      utmContent: searchParams.get('utm_content') || null,
      utmTerm: searchParams.get('utm_term') || null,
    };

    // Store with timestamp for TTL calculation
    const storedAttribution: StoredAttribution = {
      ...attributionData,
      capturedAt: new Date().toISOString(),
    };

    try {
      sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(storedAttribution));
    } catch (error) {
      // Gracefully handle storage errors (private browsing, quota exceeded)
      console.warn('Failed to store attribution data:', error);
    }
  }, []); // Empty deps: run once on mount (§TS.13.1)
}

/**
 * Provides access to stored attribution data with TTL validation.
 *
 * Returns object with two methods:
 * - getAttribution(): Retrieves and validates stored attribution, returns null if expired/missing
 * - clearAttribution(): Removes attribution from sessionStorage
 *
 * TTL validation compares current time against capturedAt + ATTRIBUTION_TTL_MS.
 *
 * @returns Object with getAttribution and clearAttribution methods
 *
 * @example
 * function SignUpFlow() {
 *   const { getAttribution, clearAttribution } = useAttributionStorage();
 *
 *   const handleSubmit = async () => {
 *     const attribution = getAttribution();
 *     await submitWithAttribution(attribution);
 *     clearAttribution();
 *   };
 * }
 */
export function useAttributionStorage(): {
  getAttribution: () => AttributionData | null;
  clearAttribution: () => void;
} {
  const getAttribution = useCallback((): AttributionData | null => {
    return getStoredAttribution();
  }, []); // Empty deps: getStoredAttribution is module-level (§TS.13.1)

  const clearAttribution = useCallback((): void => {
    try {
      sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    } catch (error) {
      // Gracefully handle storage errors
      console.warn('Failed to clear attribution data:', error);
    }
  }, []); // Empty deps: sessionStorage and ATTRIBUTION_STORAGE_KEY are stable (§TS.13.1)

  return {
    getAttribution,
    clearAttribution,
  };
}

/**
 * Internal helper to retrieve and validate stored attribution.
 *
 * Handles JSON parsing errors and TTL expiration gracefully.
 * Returns null if attribution is missing, expired, or invalid.
 *
 * @returns Valid AttributionData or null
 */
function getStoredAttribution(): AttributionData | null {
  try {
    const stored = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as StoredAttribution;

    // Validate TTL
    const capturedTime = new Date(parsed.capturedAt).getTime();
    const currentTime = Date.now();
    const isExpired = currentTime - capturedTime > ATTRIBUTION_TTL_MS;

    if (isExpired) {
      // Clear expired attribution
      sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }

    // Return valid attribution (omit timestamp per AttributionData interface)
    // oxlint-disable-next-line no-unused-vars
    const { capturedAt, ...attributionData } = parsed;
    return attributionData;
  } catch (error) {
    // Handle JSON parse errors or storage errors gracefully
    console.warn('Failed to parse attribution data:', error);
    return null;
  }
}
